/* eslint-disable import/no-duplicates */
/**
 * 操作系统 / 平台图标：根据接口返回的 os_id 或名称解析 SVG。
 * 主机类图标与 dashboard overview platforms.ts 保持一致（theme-icon）。
 */
import _android from '@/pages/home/assets/img/platform/android.svg';
import _ios from '@/pages/home/assets/img/platform/ios.svg';
import _default from '@/pages/home/assets/svg/dashboard/default.svg';
import _linux from '@/pages/home/assets/svg/dashboard/linux.svg';
import _mac from '@/pages/home/assets/svg/dashboard/mac.svg';
import _web from '@/pages/home/assets/svg/dashboard/web.svg';
import _windows from '@/pages/home/assets/svg/dashboard/windows.svg';
import _nintendo from '@/pages/home/assets/svg/dataSource/theme-icon/nintendo-theme.svg';
import _playstation from '@/pages/home/assets/svg/dataSource/theme-icon/playstation-theme.svg';
import _xbox from '@/pages/home/assets/svg/dataSource/theme-icon/xbox-theme.svg';

/** 与 countries / mediaChannel 一致：键为 `_` + 标识 */
export const platformMap = {
  _default,
  _android,
  _ios,
  _web,
  _linux,
  _windows,
  _mac,
  _nintendo,
  _playstation,
  _xbox,
  /** os_id 255「所有系统」：与默认图相同，可后续替换为独立资源 */
  _all_systems: _default,
  /** os_id 256 Unknown */
  _unknown: _default,
};

export type TPlatformMapKey = keyof typeof platformMap;

type TPlatformSource = Record<string, string>;

/** 接口 os_id（字符串）→ platformMap 键 */
const OS_ID_TO_KEY: Partial<Record<string, TPlatformMapKey>> = {
  '1': '_android',
  '2': '_ios',
  '3': '_web',
  '4': '_linux',
  '5': '_windows',
  '6': '_nintendo',
  '7': '_mac',
  '8': '_playstation',
  '9': '_xbox',
  '255': '_all_systems',
  '256': '_unknown',
};

const normalize = (s: string): string => s.trim().toLowerCase();

/**
 * 根据 os_id（优先）或 os 名称（英文/中文常见写法）解析 platformMap 的键。
 */
export const resolvePlatformIconKey = (
  code: string,
  nameHint?: string,
): TPlatformMapKey => {
  const id = (code || '').trim();
  if (id && /^\d+$/.test(id)) {
    const byId = OS_ID_TO_KEY[id];
    if (byId) {
      return byId;
    }
  }

  const n = normalize(id || nameHint || '');
  if (!n) {
    return '_default';
  }

  const exact: Partial<Record<string, TPlatformMapKey>> = {
    android: '_android',
    ios: '_ios',
    web: '_web',
    linux: '_linux',
    windows: '_windows',
    mac: '_mac',
    nintendo: '_nintendo',
    playstation: '_playstation',
    xbox: '_xbox',
    'x box': '_xbox',
    unknown: '_unknown',
    'all systems': '_all_systems',
    '所有系统': '_all_systems',
  };

  if (exact[n]) {
    return exact[n]!;
  }

  if (n.includes('android')) {
    return '_android';
  }
  if (n.includes('ios')) {
    return '_ios';
  }
  if (n.includes('web')) {
    return '_web';
  }
  if (n.includes('linux')) {
    return '_linux';
  }
  if (n.includes('windows')) {
    return '_windows';
  }
  if (n.includes('mac')) {
    return '_mac';
  }
  if (n.includes('nintendo') || n.includes('switch')) {
    return '_nintendo';
  }
  if (n.includes('playstation') || n === 'ps') {
    return '_playstation';
  }
  if (n.includes('xbox') || n.includes('x box')) {
    return '_xbox';
  }
  if (n.includes('unknown')) {
    return '_unknown';
  }
  if (n.includes('all') && (n.includes('system') || n.includes('系统'))) {
    return '_all_systems';
  }

  return '_default';
};

/**
 * @param code - 通常为 os_id；也可直接传 os_name_en（需与 nameHint 二选一配合时传入第二参）
 * @param source - 扩展：可传入合并后的 map，未传则使用 platformMap
 */
export const getPlatformIcon = (
  code: string,
  source?: TPlatformSource,
  nameHint?: string,
): string => {
  const map = (source || platformMap) as TPlatformSource;
  const key = resolvePlatformIconKey(code, nameHint);
  return map[key] || map._default;
};

/** 接口行对象：优先按数字 os_id 解析，否则按英文名/中文名 */
export const getPlatformIconFromOs = (
  row: { os_id?: string; os_name_en?: string; os_name_ch?: string; },
  source?: TPlatformSource,
): string => {
  const id = (row.os_id ?? '').trim();
  if (id && /^\d+$/.test(id)) {
    return getPlatformIcon(id, source);
  }
  const name = row.os_name_en || row.os_name_ch || '';
  return getPlatformIcon(name, source);
};
